import { Injectable, Injector } from '@angular/core';
import { TranslateLoader } from '@ngx-translate/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, forkJoin, retry, timeout, delay, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { getApiUrl } from './utilities';
import _ from 'lodash';
import { ELanguages } from '../constants';
import { MonitoringService } from './shared/service/error-service/monitoring.service';

interface TranslationResponseInterface {
  data: {
    [key: string]: string;
  };
  success: boolean;
  date: string;
}

@Injectable()
export class CustomTranslateLoader implements TranslateLoader {
  private readonly baseURL: string = getApiUrl();
  private readonly solution: string = 'df-an';

  constructor(private http: HttpClient, private injector: Injector) {}

  getTranslation(language: string): Observable<any> {
    return this.getTranslationData(language).pipe(
      retry({
        delay: (error, retryCount) => {
          return of(null).pipe(delay(Math.min(retryCount * 5, 60) * 1000));
        },
      }),
      timeout({
        first: 3 * 24 * 60 * 60 * 1000,
        with: () => throwError(() => new Error('getTranslation timeout')),
      }),
      catchError((err) => {
        if (err instanceof Error && err.message === 'getTranslation timeout') {
          try {
            this.injector.get(MonitoringService).logException(err);
          } catch {
            console.error(err);
          }

          return of(false);
        }

        return throwError(() => err);
      }),
    );
  }

  getTranslationData(language: string): Observable<any> {
    const APIAddress = `${this.baseURL}/translations`;
    const params = {
      language,
      solution: this.solution,
    };
    const pageTitleParams = {
      language: ELanguages.EN,
      solution: this.solution,
      getPageTitles: true,
    };
    const headers = new HttpHeaders().set('scw-skip-version-check', 'true');

    return forkJoin({
      translations: this.http.get<TranslationResponseInterface>(APIAddress, { params, headers }),
      pageTitleTranslationsEn: this.http.get<TranslationResponseInterface>(APIAddress, {
        params: pageTitleParams,
        headers,
      }),
    }).pipe(
      switchMap((response) => {
        const { data, success } = response.translations;
        const { data: pageTitleData, success: pageTitleSuccess } = response.pageTitleTranslationsEn;

        const translations = {};

        if (pageTitleSuccess) {
          for (const key in pageTitleData) {
            _.set(translations, `pageTitleTranslateEN.${key}`, _.get(pageTitleData, key, null));
          }
        }

        if (success) {
          for (const key in data) {
            _.set(translations, key, _.get(data, key, null));
          }

          return of(translations);
        } else {
          throw new Error('translate req failed');
        }
      }),
      catchError(() => {
        throw new Error('translate req failed');
      }),
    );
  }
}
